
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface CharacterSpecies {
  id: string;
  name: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export const useCharacterSpecies = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: species = [], isLoading, error } = useQuery({
    queryKey: ['character-species'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('character_species')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as CharacterSpecies[];
    },
  });

  const createSpecies = useMutation({
    mutationFn: async (name: string) => {
      const maxOrder = Math.max(...species.map(s => s.display_order), -1);
      const { data, error } = await supabase
        .from('character_species')
        .insert([{ 
          name, 
          display_order: maxOrder + 1,
          is_active: true 
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-species'] });
      toast({
        title: "Success",
        description: "Species created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create species",
        variant: "destructive",
      });
    },
  });

  const updateSpecies = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CharacterSpecies> }) => {
      const { data, error } = await supabase
        .from('character_species')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-species'] });
      toast({
        title: "Success",
        description: "Species updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update species",
        variant: "destructive",
      });
    },
  });

  const deleteSpecies = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('character_species')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['character-species'] });
      toast({
        title: "Success",
        description: "Species deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete species",
        variant: "destructive",
      });
    },
  });

  return {
    species,
    isLoading,
    error,
    createSpecies,
    updateSpecies,
    deleteSpecies,
  };
};
